import { animate, style, transition, trigger } from '@angular/animations';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

import { ActivatedRoute } from '@angular/router';

import { translate, translateObject } from '@jsverse/transloco';
import { DropdownObject } from '@inmoment/the-kitchen';

import { ResponseType } from 'app/providers/semantria-api/configuration-route.service';
import {
  SemantriaConfiguration,
  ConfigurationService,
} from 'app/providers/semantria-api/configuration.service';

import { SemantriaAccountService } from 'app/providers/semantria-api/semantria-account.service';
import {
  AccountLimit,
  SemantriaFeaturesService,
} from 'app/providers/semantria-api/semantria-features.service';
import { AlertService } from 'app/services/alert.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { ConfigurationVersionTrackerService } from '../../services/configuration-version-tracker.service';
import ConfigurationSettingsSectionInfo, {
  SectionInfo,
} from './configuration-settings-section-info';

@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-settings',
  templateUrl: './configuration-settings.component.html',
  styleUrls: ['./configuration-settings.component.scss'],
  animations: [
    trigger('sectionInfoAnim', [
      transition(':enter', [style({ opacity: 0 }), animate('100ms', style({ opacity: 1 }))]),
    ]),
  ],
  standalone: false,
})
export class ConfigurationSettingsComponent implements OnInit, OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() isLoading = false;
  @Input() configuration: SemantriaConfiguration;
  @Input() summaryLimit: AccountLimit;
  @Input() isReadOnly: boolean;
  @Input() hasAutoAnalysis: boolean;

  @Output() configurationChange = new EventEmitter<SemantriaConfiguration>();
  @ViewChild('infoScroll') infoScroll?: ElementRef;

  responseType: ResponseType;
  originalResponseType: ResponseType;
  conceptTopicsThreshold;

  readonly posTypes = [
    {
      value: 'Adjective',
      label: translate('configuration-settings-component.adjective'),
      checked: false,
    },
    {
      value: 'Determiner',
      label: translate('configuration-settings-component.determiner'),
      checked: false,
    },
    { value: 'Misc', label: translate('configuration-settings-component.misc'), checked: false },
    { value: 'Noun', label: translate('configuration-settings-component.noun'), checked: false },
    {
      value: 'Symbol',
      label: translate('configuration-settings-component.symbol'),
      checked: false,
    },
    {
      value: 'Twitter',
      label: translate('configuration-settings-component.twitter'),
      checked: false,
    },
    { value: 'Verb', label: translate('configuration-settings-component.verb'), checked: false },
  ];
  public responseRadios = [
    { value: 'POLLING', label: translate('configuration-settings-component.polling') },
    { value: 'AUTORESPONSE', label: translate('configuration-settings-component.auto-response') },
    { value: 'CALLBACK', label: translate('configuration-settings-component.callback') },
  ];

  accountFeatures: string[] = [];

  sectionInfo: SectionInfo;

  showCallbackUrlDialog = false;
  deepModelTypes: DropdownObject[] = [
    { title: translate('configuration-settings-component.none'), id: 'None' },
  ];
  selectedDeepModel = 'None';

  constructor(
    private configService: ConfigurationService,
    private alertService: AlertService,
    private accountService: SemantriaAccountService,
    private featuresService: SemantriaFeaturesService,
    private route: ActivatedRoute,
    private configVersionTracker: ConfigurationVersionTrackerService
  ) {}

  async ngOnInit() {
    const accountDetails = await this.accountService.sub.latest();
    const allFeatures = await this.featuresService.getAllFeatures();

    //TODO remove the try catch once chef is updated so the endoint is accessible.
    try {
      const rawDeepModelTypes = await this.featuresService.getAllDeepModels();
      for (const model in rawDeepModelTypes) {
        if (rawDeepModelTypes[model]) {
          this.deepModelTypes.push({
            title: rawDeepModelTypes[model]['name'],
            id: rawDeepModelTypes[model]['name'],
          });
        }
      }
    } catch (e) {
      this.deepModelTypes.push({
        title: translate('configuration-settings-component.effort'),
        id: 'EFFORT',
      });
      this.deepModelTypes.push({
        title: translate('configuration-settings-component.emotion'),
        id: 'EMOTION',
      });
      this.deepModelTypes.push({
        title: translate('configuration-settings-component.intent'),
        id: 'INTENT',
      });
    }

    this.selectedDeepModel = this.configuration.deep_models
      ? this.configuration.deep_models[0]
      : 'None';

    for (const accountFeature of accountDetails.features) {
      const feature = allFeatures.find((f) => f.feature === accountFeature);
      if (feature.settings) {
        this.accountFeatures.push(...feature.settings);
      } else if (feature.feature === 'DEEP_MODELS') {
        this.accountFeatures.push(feature.feature);
      }
    }
    this.deepModelTypes = [...this.deepModelTypes];
  }

  ngOnChanges(changes) {
    if (changes.configuration) {
      this.conceptTopicsThreshold = Math.round(this.configuration.concept_topics_threshold * 100);
      this.configuration.pos_types = this.configuration.pos_types || 'None';
      for (const type of this.posTypes) {
        if (this.hasPartOfSpeech(type.value)) {
          type.checked = true;
        }
      }

      if (this.configuration.callback_url) {
        this.responseType = 'CALLBACK';
      } else if (this.configuration.is_autoresponse_enabled) {
        this.responseType = 'AUTORESPONSE';
      } else {
        this.responseType = 'POLLING';
      }
    }
  }

  hasFeaturePermission(feature) {
    return this.accountFeatures.includes(feature);
  }

  updateResponseType() {
    const originalUrl = this.configuration.callback_url;
    const originalAutoresponse = this.configuration.is_autoresponse_enabled;

    if (this.responseType === 'POLLING' || this.responseType === 'AUTORESPONSE') {
      this.configuration.callback_url = null;
      this.configuration.is_autoresponse_enabled = this.responseType === 'AUTORESPONSE';

      this.updateConfiguration(() => {
        this.configuration.callback_url = originalUrl;
        this.configuration.is_autoresponse_enabled = originalAutoresponse;
      });
    } else {
      this.onEditCallbackUrl();
    }
  }

  get responseTypeTooltip() {
    if (this.hasAutoAnalysis) {
      return translate('configuration-settings-component.must-be-polling');
    }
  }

  onEditCallbackUrl() {
    this.showCallbackUrlDialog = true;
    const originalUrl = this.configuration.callback_url;
    const originalAutoresponse = this.configuration.is_autoresponse_enabled;

    if (originalAutoresponse) {
      this.originalResponseType = 'AUTORESPONSE';
    } else if (originalUrl) {
      this.originalResponseType = 'CALLBACK';
    } else {
      this.originalResponseType = 'POLLING';
    }
  }

  onCloseCallbackUrlDialog(callbackUrl: string) {
    this.showCallbackUrlDialog = false;
    const originalUrl = this.configuration.callback_url;
    const originalAutoresponse = this.configuration.is_autoresponse_enabled;
    if (callbackUrl) {
      this.configuration.is_autoresponse_enabled = false;
      this.configuration.callback_url = callbackUrl;

      this.updateConfiguration(() => {
        this.configuration.callback_url = originalUrl;
        this.configuration.is_autoresponse_enabled = originalAutoresponse;
      });
    } else {
      this.responseType = this.originalResponseType;
    }
  }

  updateProperty(prop, $event) {
    $event.stopImmediatePropagation();

    let newValue;
    if (prop === 'concept_topics_threshold') {
      newValue = +$event.target.value / 100;
    } else {
      newValue =
        typeof this.configuration[prop] === 'number' ? +$event.target.value : $event.target.value;
    }
    this.updatePropertyValue(prop, newValue);
  }

  updatePropertyValue(prop, value) {
    const originalProp = this.configuration[prop];

    if (originalProp !== value) {
      this.configuration[prop] = value;
      this.updateConfiguration(() => (this.configuration[prop] = originalProp));
    }
  }

  updateSectionLvlSentiment(prop: string, event) {
    const value = event ? 'ALL' : 'NONE';
    const originalProp = this.configuration[prop];
    if (!Array.isArray(originalProp) || !originalProp.includes(value)) {
      this.configuration[prop] = [value];
      this.updateConfiguration(() => (this.configuration[prop] = originalProp));
    }
  }

  hasSetting(value) {
    return this.hasPropertyValue('settings', value);
  }

  hasPropertyValue(prop, value): boolean {
    return !!this.configuration[prop]?.includes(value);
  }

  updateSetting(prop) {
    this.toggleSetting(prop);

    this.updateConfiguration(() => this.toggleSetting(prop));
  }

  private toggleSetting(prop) {
    if (this.hasSetting(prop)) {
      this.configuration.settings = this.configuration.settings.filter((s) => s !== prop);
    } else {
      if (!this.configuration.settings) {
        this.configuration.settings = [];
      }
      this.configuration.settings.push(prop);
    }
  }

  private hasPartOfSpeech(part) {
    return (
      this.configuration.pos_types === 'All' ||
      this.configuration.pos_types.includes(part) ||
      this.configuration.pos_types.includes(part + 's')
    );
  }

  updatePartOfSpeech(part) {
    const originalPos = this.configuration.pos_types;

    if (this.hasPartOfSpeech(part)) {
      const source =
        this.configuration.pos_types === 'All'
          ? this.posTypes.map((item) => item.value).join()
          : this.configuration.pos_types;

      this.configuration.pos_types = source
        .split(',')
        .filter((p) => p !== part && p !== part + 's')
        .join();

      if (!this.configuration.pos_types.length) {
        this.configuration.pos_types = 'None';
      }
    } else {
      if (this.configuration.pos_types === 'None') {
        this.configuration.pos_types = part;
      } else {
        this.configuration.pos_types += `,${part}`;

        if (this.configuration.pos_types.split(',').length === this.posTypes.length) {
          this.configuration.pos_types = 'All';
        }
      }
    }

    this.updateConfiguration(() => (this.configuration.pos_types = originalPos));
  }

  private updateConfiguration(resetFn?) {
    this.isLoading = true;
    this.configService
      .updateConfiguration(this.configuration)
      .then((res) => {
        this.isLoading = false;
        res.notes ??= '';
        res.tags ??= [];
        this.configurationChange.emit(res);
      })
      .catch((err) => {
        this.isLoading = false;

        resetFn();
        const message = err.error
          ? err.error.error
          : translate('configuration-settings-component.unknown-error');
        this.alertService.showErrorToast(
          translate('configuration-settings-component.failed-to-save', { message })
        );
      });

    if (this.configService.hasEvaluationConfigInLocalStorage(this.configuration)) {
      this.configService
        .updateEvaluationConfiguration(this.configuration)
        .then((res) => this.configVersionTracker.setConfiguration(res));
    }
  }

  openSectionInfo(section: string) {
    if (this.sectionInfo?.id === section) {
      this.sectionInfo = undefined;
    } else {
      const link = ConfigurationSettingsSectionInfo.links[section] || {};
      const translations = translateObject(`configuration-settings-section-info.${section}`);
      this.sectionInfo = {
        id: section,
        header: translations['header'],
        summary: translations['summary'],
        link: link?.link ?? '',
        settings: Object.keys(translations['settings']).map((k) => ({
          name: translations['settings'][k]['name'],
          description: translations['settings'][k]['description'],
          link: link?.settings?.[k]?.link ?? '',
        })),
      };
      this.infoScroll?.nativeElement.scrollTo(0, 0);
    }
  }

  closeSectionInfo() {
    this.sectionInfo = undefined;
  }

  handleDeepModelChange(id: string) {
    if (id === 'None') {
      this.configuration.deep_models_threshold = 0;
    } else if (this.selectedDeepModel === 'None' && !this.configuration.deep_models_threshold) {
      this.configuration.deep_models_threshold = 85;
    }

    this.selectedDeepModel = id;
    this.updateDeepModelSetting(this.selectedDeepModel);
    this.updateConfiguration(() => this.updateDeepModelSetting(this.selectedDeepModel));
  }

  private updateDeepModelSetting(model) {
    this.configuration.deep_models = [];
    if (model !== 'None') {
      this.configuration.deep_models.push(model);
    }
  }

  get isConfigEnglish() {
    return this.configuration.language_id === 'en';
  }
}
